import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { Phone, Instagram, Menu, X, ChevronRight, Leaf, Scissors, Droplets, TreeDeciduous, Flower2, Send, CheckCircle, Facebook, Car, SprayCan, Sparkles } from "lucide-react";
import { Toaster, toast } from "sonner";
import TikTokIcon from "@/components/TikTokIcon";
import ContactInfo from "@/components/ContactInfo";
import ContactForm from "@/components/ContactForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Landscaping images
const WORK_IMAGES = [
  "https://customer-assets.emergentagent.com/job_b1c672f2-b91c-4baa-89f8-19872f938157/artifacts/rgrb3b4c_IMG_8656.jpeg",
  "https://customer-assets.emergentagent.com/job_b1c672f2-b91c-4baa-89f8-19872f938157/artifacts/loqdm2dh_IMG_8652.jpeg",
  "https://customer-assets.emergentagent.com/job_b1c672f2-b91c-4baa-89f8-19872f938157/artifacts/gjzw4ey3_IMG_8594.jpeg",
  "https://customer-assets.emergentagent.com/job_b1c672f2-b91c-4baa-89f8-19872f938157/artifacts/5nmlccgq_IMG_8592.jpeg",
  "https://customer-assets.emergentagent.com/job_green-grounds-2/artifacts/obc1ks9c_IMG_6557.jpeg",
];
const TEAM_IMAGE = "https://customer-assets.emergentagent.com/job_b1c672f2-b91c-4baa-89f8-19872f938157/artifacts/2oepyplq_IMG_8624.jpeg";
const LOGO_IMAGE = "https://customer-assets.emergentagent.com/job_green-grounds-2/artifacts/1simge3i_att.vdaKS8ke435BuCax6iBCAsnYOiG10qApDt-ZEBQGVuU.jpeg";

// Detailing images
const DETAIL_IMAGES = [
  "https://customer-assets.emergentagent.com/job_green-grounds-2/artifacts/u8arttwx_1000002964.jpeg",
  "https://customer-assets.emergentagent.com/job_green-grounds-2/artifacts/s25il0f7_1000003093.jpeg",
  "https://customer-assets.emergentagent.com/job_green-grounds-2/artifacts/vzucr4lp_1000003079.jpeg",
  "https://customer-assets.emergentagent.com/job_green-grounds-2/artifacts/7w0h96w2_1000003032.jpeg",
  "https://customer-assets.emergentagent.com/job_green-grounds-2/artifacts/3pj2olcy_1000003034.jpeg",
];
const DETAIL_TEAM_IMAGE = "https://customer-assets.emergentagent.com/job_green-grounds-2/artifacts/urstnv9c_ChatGPT%20Image%20May%2012%2C%202026%20at%2008_26_49%20AM.png";

const DETAIL_LOGO = "https://customer-assets.emergentagent.com/job_green-grounds-2/artifacts/14q4vyfa_Image%202.jpeg";

const LANDSCAPING_SERVICES = [
  { name: "Landscaping Services", icon: Leaf, description: "Complete landscape design and installation to transform your outdoor space" },
  { name: "Lawn Mowing", icon: Scissors, description: "Professional mowing and lawn care to keep your yard pristine" },
  { name: "Power Washing", icon: Droplets, description: "High-pressure cleaning for driveways, patios, and exterior surfaces" },
  { name: "Tree Trimming", icon: TreeDeciduous, description: "Expert pruning and trimming for healthy, beautiful trees" },
  { name: "Garden Maintenance", icon: Flower2, description: "Weeding, mulching, and seasonal care for thriving gardens" },
];

const DETAILING_SERVICES = [
  { name: "Exterior Detail", icon: SprayCan, description: "Full exterior wash, clay bar, polish, and wax for a showroom finish" },
  { name: "Interior Detail", icon: Car, description: "Deep clean of seats, carpets, dash, and all interior surfaces" },
  { name: "Full Detail Package", icon: Sparkles, description: "Complete interior and exterior detailing for the ultimate transformation" },
];

// Nav sub-components
const NavLink = ({ onClick, label, testId, hoverColor = "hover:text-[#C4D6B0]" }) => (
  <button onClick={onClick} className={`text-[#A3A8A3] ${hoverColor} transition-colors text-xs`} data-testid={testId}>{label}</button>
);

const NavSocials = () => (
  <div className="hidden lg:flex items-center gap-3">
    <a href="https://instagram.com/rsg_landscaping" target="_blank" rel="noopener noreferrer" className="text-[#A3A8A3] hover:text-[#C4D6B0] transition-colors">
      <Instagram className="w-4 h-4" />
    </a>
    <a href="https://tiktok.com/@rsg_landscaping" target="_blank" rel="noopener noreferrer" className="text-[#A3A8A3] hover:text-[#C4D6B0] transition-colors">
      <TikTokIcon className="w-4 h-4" />
    </a>
    <a href="https://www.facebook.com/profile.php?id=61584987848634" target="_blank" rel="noopener noreferrer" className="text-[#A3A8A3] hover:text-[#C4D6B0] transition-colors">
      <Facebook className="w-4 h-4" />
    </a>
    <a href="tel:4196317496" className="btn-cta px-4 py-2 rounded-full flex items-center gap-2 text-sm" data-testid="nav-call-btn">
      <Phone className="w-3 h-3" /> Call Now
    </a>
  </div>
);

const MobileMenu = ({ scrollToSection }) => (
  <div className="lg:hidden bg-[#0A0C0A] border-t border-white/10 p-6 space-y-3" data-testid="mobile-menu">
    <p className="text-[#C4D6B0] font-semibold text-xs uppercase tracking-widest">Landscaping</p>
    <button onClick={() => scrollToSection('services')} className="block w-full text-left text-[#F2F0E9] py-2">Services</button>
    <button onClick={() => scrollToSection('work')} className="block w-full text-left text-[#F2F0E9] py-2">Our Work</button>
    <button onClick={() => scrollToSection('team')} className="block w-full text-left text-[#F2F0E9] py-2">Team</button>
    <div className="border-t border-white/10 pt-3 mt-3" />
    <p className="text-[#93B8D8] font-semibold text-xs uppercase tracking-widest">Detailing</p>
    <button onClick={() => scrollToSection('detail-services')} className="block w-full text-left text-[#F2F0E9] py-2">Services</button>
    <button onClick={() => scrollToSection('detail-work')} className="block w-full text-left text-[#F2F0E9] py-2">Our Work</button>
    <button onClick={() => scrollToSection('detail-team')} className="block w-full text-left text-[#F2F0E9] py-2">Team</button>
    <div className="border-t border-white/10 pt-3 mt-3" />
    <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-[#F2F0E9] py-2">Contact</button>
    <div className="flex gap-3 pt-3">
      <a href="tel:4196317496" className="btn-cta flex-1 py-3 rounded-full flex items-center justify-center gap-2 text-sm">
        <Phone className="w-4 h-4" />Landscaping
      </a>
      <a href="tel:4196881398" className="btn-cta-blue flex-1 py-3 rounded-full flex items-center justify-center gap-2 text-sm">
        <Phone className="w-4 h-4" />Detailing
      </a>
    </div>
  </div>
);

// Navigation
const Navigation = ({ isMenuOpen, setIsMenuOpen }) => {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 w-full z-50 nav-glass" data-testid="navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center gap-3" data-testid="logo">
            <img src={LOGO_IMAGE} alt="RSG Logo" className="h-12 w-12 rounded-lg object-cover" />
            <span className="text-xl font-bold text-[#F2F0E9] font-['Outfit']">RSG</span>
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <span className="text-[#C4D6B0] font-semibold text-xs">LANDSCAPING</span>
            <NavLink onClick={() => scrollToSection('services')} label="Services" testId="nav-services" />
            <NavLink onClick={() => scrollToSection('work')} label="Work" testId="nav-work" />
            <NavLink onClick={() => scrollToSection('team')} label="Team" testId="nav-team" />
            <div className="w-px h-5 bg-white/20" />
            <span className="text-[#93B8D8] font-semibold text-xs">DETAILING</span>
            <NavLink onClick={() => scrollToSection('detail-services')} label="Services" testId="nav-detail-services" hoverColor="hover:text-[#93B8D8]" />
            <NavLink onClick={() => scrollToSection('detail-work')} label="Work" testId="nav-detail-work" hoverColor="hover:text-[#93B8D8]" />
            <NavLink onClick={() => scrollToSection('detail-team')} label="Team" testId="nav-detail-team" hoverColor="hover:text-[#93B8D8]" />
            <div className="w-px h-5 bg-white/20" />
            <NavLink onClick={() => scrollToSection('contact')} label="Contact" testId="nav-contact" hoverColor="hover:text-[#F2F0E9]" />
          </div>

          <NavSocials />

          <button className="lg:hidden text-[#F2F0E9]" onClick={() => setIsMenuOpen(!isMenuOpen)} data-testid="mobile-menu-toggle">
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {isMenuOpen && <MobileMenu scrollToSection={scrollToSection} />}
    </nav>
  );
};

// Landscaping Hero
const HeroSection = () => (
  <section className="min-h-screen flex items-center relative overflow-hidden" data-testid="hero-section">
    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${WORK_IMAGES[0]})` }} />
    <div className="hero-overlay absolute inset-0" />
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
      <div className="max-w-3xl">
        <p className="accent-label mb-6 fade-in-up">Serving Bellville, Ohio & Surrounding Areas</p>
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#F2F0E9] leading-[1.1] mb-6 fade-in-up delay-100" data-testid="hero-title">
          Reliable. Clean.<br /><span className="text-[#C4D6B0]">Professional</span> Landscaping.
        </h1>
        <p className="text-xl text-[#A3A8A3] mb-10 max-w-xl fade-in-up delay-200">
          Transform your outdoor space with expert landscaping services. From lawn care to complete garden transformations.
        </p>
        <div className="flex flex-wrap gap-4 fade-in-up delay-300">
          <button onClick={() => document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' })} className="btn-cta px-8 py-4 rounded-full flex items-center gap-2 text-lg" data-testid="hero-quote-btn">
            Get Free Quote <ChevronRight className="w-5 h-5" />
          </button>
          <a href="tel:4196317496" className="btn-secondary px-8 py-4 rounded-full flex items-center gap-2 text-lg" data-testid="hero-call-btn">
            <Phone className="w-5 h-5" /> 419-631-7496
          </a>
        </div>
      </div>
    </div>
  </section>
);

// Landscaping Services
const ServicesSection = () => (
  <section id="services" className="py-24 bg-[#0A0C0A]" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <p className="accent-label mb-4">What We Do</p>
        <h2 className="text-4xl sm:text-5xl font-bold text-[#F2F0E9]" data-testid="services-title">Landscaping Services</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {LANDSCAPING_SERVICES.map((service, index) => (
          <div key={service.name} className="service-card p-8 rounded-2xl" data-testid={`service-card-${index}`}>
            <div className="w-14 h-14 bg-[#2D4A22] rounded-xl flex items-center justify-center mb-6">
              <service.icon className="w-7 h-7 text-[#D9E8C8]" />
            </div>
            <h3 className="text-xl font-semibold text-[#F2F0E9] mb-3">{service.name}</h3>
            <p className="text-[#A3A8A3] leading-relaxed">{service.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Landscaping Gallery
const WorkGallery = () => (
  <section id="work" className="py-24 bg-[#111411]" data-testid="work-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <p className="accent-label mb-4">Portfolio</p>
        <h2 className="text-4xl sm:text-5xl font-bold text-[#F2F0E9]" data-testid="work-title">Our Work</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {WORK_IMAGES.map((img, index) => (
          <div key={img} className={`gallery-item ${index === 0 ? 'md:col-span-2 h-[400px]' : 'h-[300px]'}`} data-testid={`gallery-item-${index}`}>
            <img src={img} alt={`RSG Landscaping work ${index + 1}`} className="w-full h-full object-cover" />
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Landscaping Team
const TeamSection = () => (
  <section id="team" className="py-24 bg-[#0A0C0A]" data-testid="team-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="accent-label mb-4">About Us</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-[#F2F0E9] mb-6" data-testid="team-title">Meet the Team</h2>
          <p className="text-lg text-[#A3A8A3] mb-6 leading-relaxed">
            RSG Landscaping is locally owned and operated out of Bellville, Ohio. We take pride in every job,
            treating your property like our own. Our dedicated team brings passion and
            expertise to transform outdoor spaces across Bellville and surrounding areas.
          </p>
          <div className="flex flex-wrap gap-6">
            {["Locally Owned", "Fully Insured", "Free Estimates"].map(item => (
              <div key={item} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#C4D6B0]" />
                <span className="text-[#F2F0E9]">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="gallery-item rounded-2xl overflow-hidden h-[400px] lg:h-[500px]">
          <img src={TEAM_IMAGE} alt="RSG Landscaping Team" className="w-full h-full object-cover" data-testid="team-image" />
        </div>
      </div>
    </div>
  </section>
);

// Quote form field components
const QuoteFormFields = ({ formData, handleChange }) => (
  <>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <input type="text" name="name" placeholder="Your Name *" required value={formData.name} onChange={handleChange} className="form-input w-full px-5 py-4 rounded-xl" data-testid="quote-name-input" />
      <input type="email" name="email" placeholder="Email Address *" required value={formData.email} onChange={handleChange} className="form-input w-full px-5 py-4 rounded-xl" data-testid="quote-email-input" />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <input type="tel" name="phone" placeholder="Phone Number *" required value={formData.phone} onChange={handleChange} className="form-input w-full px-5 py-4 rounded-xl" data-testid="quote-phone-input" />
      <input type="text" name="address" placeholder="Property Address" value={formData.address} onChange={handleChange} className="form-input w-full px-5 py-4 rounded-xl" data-testid="quote-address-input" />
    </div>
    <select name="service_type" required value={formData.service_type} onChange={handleChange} className="form-input w-full px-5 py-4 rounded-xl bg-transparent" data-testid="quote-service-select">
      <option value="" className="bg-[#111411]">Select Service *</option>
      <optgroup label="Landscaping" className="bg-[#111411]">
        {LANDSCAPING_SERVICES.map(s => <option key={s.name} value={s.name} className="bg-[#111411]">{s.name}</option>)}
      </optgroup>
      <optgroup label="Detailing" className="bg-[#111411]">
        {DETAILING_SERVICES.map(s => <option key={s.name} value={s.name} className="bg-[#111411]">{s.name}</option>)}
      </optgroup>
    </select>
    <textarea name="project_description" placeholder="Describe your project *" required rows={4} value={formData.project_description} onChange={handleChange} className="form-input w-full px-5 py-4 rounded-xl resize-none" data-testid="quote-description-textarea" />
    <input type="date" name="preferred_date" value={formData.preferred_date} onChange={handleChange} className="form-input w-full px-5 py-4 rounded-xl" data-testid="quote-date-input" />
  </>
);

const EMPTY_QUOTE = { name: '', email: '', phone: '', address: '', service_type: '', project_description: '', preferred_date: '' };

// Quote Request Form
const QuoteForm = () => {
  const [formData, setFormData] = useState(EMPTY_QUOTE);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, formData);
      toast.success("Quote request submitted! We'll contact you soon.");
      setFormData(EMPTY_QUOTE);
    } catch {
      toast.error('Failed to submit. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="quote" className="py-24 bg-[#111411]" data-testid="quote-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="accent-label mb-4">Free Estimate</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-[#F2F0E9]" data-testid="quote-title">Request a Quote</h2>
          <p className="text-[#A3A8A3] mt-4">Fill out the form below and we&apos;ll get back to you within 24 hours</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
          <QuoteFormFields formData={formData} handleChange={handleChange} />
          <button type="submit" disabled={isSubmitting} className="btn-cta w-full py-4 rounded-full flex items-center justify-center gap-2 text-lg disabled:opacity-50" data-testid="quote-submit-btn">
            {isSubmitting ? 'Submitting...' : 'Request Free Quote'} <Send className="w-5 h-5" />
          </button>
        </form>
      </div>
    </section>
  );
};

// ===== DETAILING SECTIONS (Blue Theme) =====

// Detailing Divider/Hero
const DetailingHero = () => (
  <section className="py-20 relative overflow-hidden" data-testid="detailing-hero">
    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${DETAIL_IMAGES[0]})` }} />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0A0C0A]/90 via-[#0A0C0A]/70 to-[#0A0C0A]/90" />
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <img src={DETAIL_LOGO} alt="RSG Detailing Logo" className="w-32 h-32 mx-auto mb-6 rounded-2xl object-cover" data-testid="detail-logo" />
      <p className="accent-label-blue mb-4">Now Offering</p>
      <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#F2F0E9] mb-4" data-testid="detailing-hero-title">
        RSG <span className="text-[#93B8D8]">Detailing</span>
      </h2>
      <p className="text-xl text-[#A3A8A3] max-w-2xl mx-auto mb-8">
        Premium auto detailing services. From exterior wash to full ceramic coating — your vehicle deserves the best.
      </p>
      <a href="tel:4196881398" className="btn-cta-blue px-8 py-4 rounded-full inline-flex items-center gap-2 text-lg" data-testid="detailing-call-btn">
        <Phone className="w-5 h-5" /> 419-688-1398
      </a>
    </div>
  </section>
);

// Detailing Services
const DetailingServices = () => (
  <section id="detail-services" className="py-24 bg-[#0A0C0A]" data-testid="detail-services-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <p className="accent-label-blue mb-4">What We Offer</p>
        <h2 className="text-4xl sm:text-5xl font-bold text-[#F2F0E9]" data-testid="detail-services-title">Detailing Services</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {DETAILING_SERVICES.map((service, index) => (
          <div key={service.name} className="detail-service-card p-8 rounded-2xl" data-testid={`detail-service-card-${index}`}>
            <div className="w-14 h-14 bg-[#1A2F4A] rounded-xl flex items-center justify-center mb-6">
              <service.icon className="w-7 h-7 text-[#93B8D8]" />
            </div>
            <h3 className="text-xl font-semibold text-[#F2F0E9] mb-3">{service.name}</h3>
            <p className="text-[#A3A8A3] leading-relaxed">{service.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Detailing Gallery
const DetailingGallery = () => (
  <section id="detail-work" className="py-24 bg-[#0D1117]" data-testid="detail-work-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <p className="accent-label-blue mb-4">Portfolio</p>
        <h2 className="text-4xl sm:text-5xl font-bold text-[#F2F0E9]" data-testid="detail-work-title">Our Detailing Work</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {DETAIL_IMAGES.map((img, index) => (
          <div key={img} className={`gallery-item ${index === 0 ? 'md:col-span-2 h-[400px]' : 'h-[300px]'}`} data-testid={`detail-gallery-item-${index}`}>
            <img src={img} alt={`RSG Detailing work ${index + 1}`} className="w-full h-full object-cover" />
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Detailing Team
const DetailingTeam = () => (
  <section id="detail-team" className="py-24 bg-[#0A0C0A]" data-testid="detail-team-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div className="gallery-item rounded-2xl overflow-hidden h-[400px] lg:h-[500px]">
          <img src={DETAIL_TEAM_IMAGE} alt="RSG Detailing Team" className="w-full h-full object-cover" data-testid="detail-team-image" />
        </div>
        <div>
          <p className="accent-label-blue mb-4">About</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-[#F2F0E9] mb-6" data-testid="detail-team-title">Meet RSG Detailing</h2>
          <p className="text-lg text-[#A3A8A3] mb-6 leading-relaxed">
            RSG Detailing brings the same dedication and attention to detail that defines RSG Landscaping — now for your vehicle.
            Based in Bellville, Ohio, we treat every car like it&apos;s our own.
          </p>
          <p className="text-lg text-[#A3A8A3] mb-8 leading-relaxed">
            Whether it&apos;s a quick exterior wash or a full ceramic coating, we deliver results that speak for themselves.
          </p>
          <div className="flex flex-wrap gap-6">
            {["Mobile Service", "Premium Products", "Satisfaction Guaranteed"].map(item => (
              <div key={item} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#93B8D8]" />
                <span className="text-[#F2F0E9]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Contact Section (All businesses)
const ContactSection = () => (
  <section id="contact" className="py-24 bg-[#0A0C0A]" data-testid="contact-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-16">
        <div>
          <p className="accent-label mb-4">Get In Touch</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-[#F2F0E9] mb-8" data-testid="contact-title">Contact Us</h2>
          <ContactInfo />
        </div>
        <ContactForm />
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="bg-[#050605] py-16 border-t border-white/10" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row justify-between items-center gap-8">
        <div className="flex items-center gap-3">
          <img src={LOGO_IMAGE} alt="RSG Landscaping Logo" className="h-12 w-12 rounded-lg object-cover" />
          <span className="text-lg font-bold text-[#F2F0E9] font-['Outfit']">RSG Landscaping</span>
          <div className="w-px h-8 bg-white/20 mx-2" />
          <img src={DETAIL_LOGO} alt="RSG Detailing Logo" className="h-12 w-12 rounded-lg object-cover" />
          <span className="text-lg font-bold text-[#F2F0E9] font-['Outfit']">RSG Detailing</span>
        </div>
        <div className="flex flex-wrap items-center gap-6">
          <a href="tel:4196317496" className="text-[#A3A8A3] hover:text-[#C4D6B0] transition-colors flex items-center gap-2 text-sm">
            <Phone className="w-4 h-4" /> Landscaping: 419-631-7496
          </a>
          <a href="tel:4196881398" className="text-[#A3A8A3] hover:text-[#93B8D8] transition-colors flex items-center gap-2 text-sm">
            <Phone className="w-4 h-4" /> Detailing: 419-688-1398
          </a>
          <div className="flex items-center gap-3">
            <a href="https://instagram.com/rsg_landscaping" target="_blank" rel="noopener noreferrer" className="text-[#A3A8A3] hover:text-[#C4D6B0] transition-colors">
              <Instagram className="w-4 h-4" />
            </a>
            <a href="https://tiktok.com/@rsg_landscaping" target="_blank" rel="noopener noreferrer" className="text-[#A3A8A3] hover:text-[#C4D6B0] transition-colors">
              <TikTokIcon className="w-4 h-4" />
            </a>
            <a href="https://www.facebook.com/profile.php?id=61584987848634" target="_blank" rel="noopener noreferrer" className="text-[#A3A8A3] hover:text-[#C4D6B0] transition-colors">
              <Facebook className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
      <div className="section-divider mt-12 mb-8" />
      <p className="text-center text-[#A3A8A3] text-sm" data-testid="footer-copyright">© 2026 RSG Landscaping & RSG Detailing. All rights reserved.</p>
    </div>
  </footer>
);

// Main App
function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0A0C0A]">
      <Toaster position="top-right" richColors />
      <Navigation isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      {/* Landscaping */}
      <HeroSection />
      <ServicesSection />
      <WorkGallery />
      <TeamSection />
      <QuoteForm />
      {/* Detailing */}
      <DetailingHero />
      <DetailingServices />
      <DetailingGallery />
      <DetailingTeam />
      {/* Shared */}
      <ContactSection />
      <Footer />
    </div>
  );
}

export default App;
